import { useState, useEffect, useId, Fragment } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Alert, Button, Card, CardActions, Grid, Stack, Tab } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ChartsDataProvider } from '@mui/x-charts/ChartsDataProvider';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import { LinePlot } from '@mui/x-charts/LineChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import { useParams } from 'react-router';
import {TabContext, TabList} from '@mui/lab';
import { styled } from '@mui/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'flex-start',
  color: theme.palette.secondary.main,
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

function RecessionBands( {periods, color} ) {
  const { top, left, width, height } = useDrawingArea();
  const xScale = useXScale();
  const theme = useTheme();
  const labelFill = alpha(theme.palette.text.primary, 0.7);

  const xStart = xScale(periods.start);
        const xEnd = xScale(periods.end);

        if (xStart === undefined || xEnd === undefined) {
          return null;
        }

        let textX;
        if (xStart >= left && xStart <= left + width) {
          textX = xStart;
        } else if (xEnd >= left && xEnd <= left + width) {
          textX = left;
        } else {
          return null;
        }

  return (
    <g>
      
          <Fragment key={1}>
            <rect
              x={textX}
              y={top}
              width={Math.min(xEnd, left + width) - textX}
              height={height}
              fill={color}
              opacity={0.2}
            />
            <text
              x={textX}
              y={top - 5}
              textAnchor="start"
              dominantBaseline="auto"
              fill={labelFill}
              fontSize="0.8rem"
              fontWeight={500}
              pointerEvents="none"
              wordBreak="break-word"
            >
              Empfehlung
            </text>
          </Fragment>
        
    </g>
  );
}

const RecommendationGraph = ({label, exampleData, id, perfekt, sub, xLabel, backgroundColor}) => {
  const theme = useTheme();
  return (
    <StyledCard theme={theme} sx={{'background-color': backgroundColor}}>
    <Box sx={{ flexGrow: 1 }} className="recommendation-graph">
       <Typography textAlign="center">
        {label}
      </Typography>

      <ChartsDataProvider
        height={300}
        experimentalFeatures={{ preferStrictDomainInLineCharts: true }}
        dataset={exampleData}
        series={[
          {
            type: 'line',
            id: 'unemployment',
            dataKey: 'LOW',
            label: 'Gering',
            color: '#0000ff',
            showMark: false,
            yAxisId: 'anteil',
            valueFormatter: (value) => (value == null ? '' : `${value.toFixed(1)}%`),
          },
          {
            type: 'line',
            dataKey: 'OK',
            label: 'Ok',
            color: '#00ff00',
            showMark: false,
            yAxisId: 'anteil',
            connectNulls: true,
            valueFormatter: (value) =>
              value == null ? '' : `${value.toFixed(1)}%`,
          },
          {
            type: 'line',
            dataKey: 'HIGH',
            label: 'zu Hoch',
            color: '#ff0000',
            showMark: false,
            yAxisId: 'anteil',
            connectNulls: true,
            valueFormatter: (value) =>
              value == null ? '' : `${value.toFixed(1)}%`,
          },
        ]}
        xAxis={[
          {
            id: 'x',
            scaleType: 'linear',
            dataKey: 'value',
            tickNumber: 4,
            label: 'temperature',
            valueFormatter: (temp, context) => {
              return temp + ' ' + xLabel;
            },
            
          },
        ]}
        yAxis={[
          {
            id: 'anteil',
            scaleType: 'linear',
            valueFormatter: (value) => `${value}%`,
            width: 60,
            position: 'left',
          },
          
        ]}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <ChartsLegend />
        </Box>
        <ChartsSurface>
          <ChartsClipPath id={id} />
          <RecessionBands periods={perfekt} color='grey' text='Recipe'/>
          <ChartsGrid horizontal />
          <g clipPath={`url(#${id})`}>
            <LinePlot />
          </g>
          <ChartsXAxis axisId= 'x' label={sub}/>
          <ChartsYAxis axisId="anteil" label="Anteil" />
          <ChartsAxisHighlight x="line" />
        </ChartsSurface>
        <ChartsTooltip />
      </ChartsDataProvider>
    </Box>
    </StyledCard>
  );
}

const Recommendation = () => {
    const theme = useTheme();
    const [errorMessage, setErrorMessage] = useState(null);
    const [hintText, setHintText] = useState('Statistiken für das Produkt bei allen Usern, unabhängig von Fryer und Präferenz.');
    const [recommendationTemperature, setRecommendationTemperature] = useState([]);
    const [recommendationDuration, setRecommendationDuration] = useState([]);
    const [recommendationInnerTemperature, setRecommendationInnerTemperature] = useState([]);
    const [recommendationInnerDuration, setRecommendationInnerDuration] = useState([]);
    const [perfectTemperatur, setPerfectTemperatur] = useState({start: null, end: null});
    const [perfectDuration, setPerfectDuration] = useState({start: null, end: null});
    const [product, setProduct] = useState(null);
    const [tabValue, setTabValue] = useState('1');
    const [json, setJson] = useState(null);
    const {id} = useParams();
    useEffect(() => {
      load(id);
    }, [id]);

    useEffect(() => {
      tabChange(null, tabValue);
    }, [json]);

    const clipPathId = useId();

    const load = async (id) => {
      const headers = new Headers();
      headers.append('Content-Type', 'accplication/json');
      const requestOptions = {
        method: 'GET',
        headers: headers
      };
      const response = await fetch('/api/recommendation?productId='+ id, requestOptions);
      const jsonResponse = await response.json();
      if(response.ok){
        setErrorMessage(null);
        setJson(jsonResponse);
        setProduct(jsonResponse.product.name);
        setPerfectTemperatur({start: jsonResponse.temperature - 3, end: jsonResponse.temperature + 3});
        setPerfectDuration({start: jsonResponse.duration - 0.5, end: jsonResponse.duration + 0.5});
      }else{
        setErrorMessage(jsonResponse.message);
      }
    }

    const f = p => {
      return {
        value: p.value,
        LOW: p.crispynessDists.LOW,
        OK: p.crispynessDists.OK,
        HIGH: p.crispynessDists.HIGH
      };
    }
    
    const tabChange = (evt, newValue) => {
      setTabValue(newValue);
      if(json === null) return;
      if(newValue === '1'){
        setRecommendationTemperature(json.productTempDtos.map(p => f(p)));
        setRecommendationInnerTemperature(json.productTempInnerDtos.map(p => f(p)));
        setRecommendationDuration(json.productDurDtos.map(p => f(p)));
        setRecommendationInnerDuration(json.productDurInnerDtos.map(p => f(p)));
        setHintText('Statistiken für das Produkt bei allen Usern, unabhängig von Fryer und Präferenz.');
      }else{
        setRecommendationTemperature(json.userTempDtos.map(p => f(p)));
        setRecommendationInnerTemperature(json.userTempInnerDtos.map(p => f(p)));
        setRecommendationDuration(json.userDurDtos.map(p => f(p)));
        setRecommendationInnerDuration(json.userDurInnerDtos.map(p => f(p)));
        setHintText('Statistiken für deine persönlichen Erfahrungen mit dem Produkt.');
      }
    }

    return ( 
      <Box sx={{ flexGrow: 1, marginTop: '10px !important' }}>
      <Box sx={{ overflow: "auto" }}>
    <div class="centered">
      <h2 className="left-header">{product}</h2>{!errorMessage && <div>
    <StyledCard theme={theme} className="custom-grid">
      <Typography fontSize={20} variant="body2" sx={{ color: 'secondary', lineHeight: 2 }}>
          <strong>Deine Empfehlung</strong>
      </Typography>
      <Typography fontSize={22} variant="body2" sx={{ color: 'secondary', lineHeight: 2 }}><strong>Temperatur:</strong> {perfectTemperatur.start + 3} °C</Typography>
      <Typography fontSize={22} variant="body2" sx={{ color: 'secondary', lineHeight: 2 }}><strong>Dauer:</strong> {perfectDuration.start + 0.5} Minuten</Typography>

    </StyledCard>
    <TabContext value={tabValue}>
      <TabList variant="fullWidth" onChange={tabChange}>
        <Tab label="Produkt" value="1"/>
        <Tab label="User" value="2"/*disable if no values are present*//>
      </TabList>
    </TabContext>
    <Stack direction="column" spacing={2} >
      <div className="crispyness">{hintText}<br/><br/><strong>Hinweis:</strong> die Diagramme sind noch im Aufbau.</div>
      <h4 className="crispyness">Krossheit</h4>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: "flex-start",
          alignItems: "stretch"
        }}
        spacing={4}
      >
        <RecommendationGraph backgroundColor={'#FFE0DA'} exampleData={recommendationTemperature} id={clipPathId} perfekt={perfectTemperatur} label="Temperatur-Empfehlung" xLabel="°C" sub="Temperatur"/>
        <RecommendationGraph backgroundColor={'#FFE0DA'} exampleData={recommendationDuration} id={clipPathId} perfekt={perfectDuration} label="Zeit-Empfehlung" xLabel="Minuten" sub="Dauer"/>
      </Grid>
    </Stack>
    
    <Stack direction="column" spacing={2}>
      <h4 className="crispyness">Innere Krossheit</h4>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: "flex-start",
          alignItems: "stretch"
        }}
        spacing={4}
      >
        <RecommendationGraph backgroundColor={'#D0E0F0'} exampleData={recommendationInnerTemperature} id={clipPathId} perfekt={perfectTemperatur} label="Temperatur-Empfehlung" xLabel="°C" sub="Temperatur"/>
        <RecommendationGraph backgroundColor={'#D0E0F0'} exampleData={recommendationInnerDuration} id={clipPathId} perfekt={perfectDuration} label="Zeit-Empfehlung" xLabel="Minuten" sub="Dauer"/>
      </Grid>
    </Stack>
    </div>}
    {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
    </div>
    </Box>
    </Box>
  );
}

export default Recommendation;